import axios from 'axios'
import { eventBus } from '../../eventBus'

/** Single-file uploads stay under this; larger files use chunked upload when enabled. */
export const SMARTINTAKE_SINGLE_FILE_MAX_BYTES = 32 * 1024 * 1024
/** Max raw byte size per chunked part (no re-encoding). */
export const SMARTINTAKE_CHUNK_PART_MAX_BYTES = 20 * 1024 * 1024
/** SmartIntake allows at most this many parts. */
export const SMARTINTAKE_MAX_PARTS = 50
/** Hard ceiling for a chunked upload: 50 × 20 MB. */
export const SMARTINTAKE_MAX_FILE_BYTES = SMARTINTAKE_MAX_PARTS * SMARTINTAKE_CHUNK_PART_MAX_BYTES

/** @deprecated use SMARTINTAKE_SINGLE_FILE_MAX_BYTES */
const SINGLE_FILE_MAX_BYTES = SMARTINTAKE_SINGLE_FILE_MAX_BYTES
const CHUNK_PART_MAX_BYTES = SMARTINTAKE_CHUNK_PART_MAX_BYTES
const MAX_PARTS = SMARTINTAKE_MAX_PARTS
const MAX_FILE_BYTES = SMARTINTAKE_MAX_FILE_BYTES
/** Middle parts may be sent concurrently. */
const MIDDLE_PART_CONCURRENCY = 4
/** Retries for parts other than part 1. */
const PART_MAX_RETRIES = 3
/** Long-running analysis on the last part (or single-file upload). */
const ANALYZE_TIMEOUT_MS = 1240000

/**
 * SmartIntakeAIUploader - Handles audio upload and analysis via SmartIntakeAI API
 *
 * Implements the SmartIntakeAI workflow:
 * 1. Request public token from backend
 * 2. Upload audio (single-file or chunked) for analysis
 * 3. Receive analysis results (via webhook or polling)
 *
 * Implements TranscriptionServiceInterface:
 * - async uploadData(file, ...args)
 */
class SmartIntakeAIUploader {
  MAX_RETRIES = 3
  axiosClient = null
  store = null

  constructor (axiosClient, store) {
    this.axiosClient = axiosClient
    this.store = store
  }

  /**
   * Uploads audio data to SmartIntakeAI for analysis.
   *
   * Templates are pre-configured in admin settings.
   *
   * Implements TranscriptionServiceInterface.uploadData(file, ...args)
   *
   * @param {File} file - The audio file to be analyzed
   * @param {...*} args - Additional arguments: [fileName, progressToast, transcriptionService, multiStepTracker]
   * @returns {Promise<Object>} Analysis result with publicToken and analysis data
   */
  async uploadData (file, ...args) {
    const [fileName, progressToast, transcriptionService, multiStepTracker, uploadOptions = {}] = args
    const chunkedUploadEnabled = uploadOptions.chunkedUploadEnabled === true

    // Use MultiStepUpload component if available, fallback to toast
    let progressTracker
    if (multiStepTracker) {
      progressTracker = new MultiStepProgressAdapter(multiStepTracker)
      multiStepTracker.start()
    } else if (progressToast && typeof progressToast.toast === 'function') {
      progressTracker = new UploadProgressTracker(progressToast)
      progressToast.toast('show')
    } else {
      progressTracker = new UploadProgressTracker(null)
    }
    progressTracker.setStage('init')

    try {
      const result = await this.analyzeAudio(file, fileName, {
        transcriptionService,
        progressToast,
        progressTracker,
        chunkedUploadEnabled
      })

      return result
    } catch (error) {
      progressTracker.error(error.message || 'Analysis failed')
      throw error
    }
  }

  /**
   * Request a public token from the backend for SmartIntakeAI workflow
   *
   * @param {Object} config - Configuration options
   * @param {string} config.webhookUrl - Optional: URL to receive analysis completion notifications
   * @param {boolean} config.deleteAfterAccessed - Optional: Delete results after first access (default: true)
   * @param {number} config.recordingDurationSeconds - Optional: Recording duration in seconds
   * @returns {Promise<{ publicToken: string, apiUrl: string }>}
   */
  async requestPublicToken (config = {}) {
    const meetingId = this.store.state?.meeting?.id
    try {
      const response = await this.axiosClient.post(
        '/transcription/smartintake/prepare',
        {
          meetingId,
          webhookUrl: config.webhookUrl || undefined,
          deleteAfterAccessed: config.deleteAfterAccessed !== false,
          recordingDurationSeconds: config.recordingDurationSeconds || undefined,
          service: 'SMARTINTAKEAI',
          isAdmin: false
        },
        {
          headers: {
            'Authorization': `Bearer ${this.store.state?.access_token}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.data.success) {
        throw new Error('Failed to create public token')
      }

      return response.data.result
    } catch (error) {
      console.error('Error requesting public token:', error)
      throw new Error(`Failed to get public token: ${error.message}`)
    }
  }

  /**
   * Upload audio file to SmartIntakeAI for analysis.
   * Uses a single request under ~32 MB; larger files use chunked upload when the
   * Flagmint flag smartintake_chunked_upload is on.
   *
   * @param {File|Blob} file - The audio file to analyze
   * @param {string} publicToken - The public token for authentication
   * @param {string} templateId - The template ID to use for analysis
   * @param {UploadProgressTracker|MultiStepProgressAdapter} progressTracker - Progress tracker instance
   * @param {string} apiBaseURL - SmartIntake API base URL
   * @param {boolean} chunkedUploadEnabled - Flagmint gate for chunked path
   * @returns {Promise<Object>} Analysis results
   */
  async uploadForAnalysis (file, publicToken, templateId, progressTracker, apiBaseURL, chunkedUploadEnabled = false) {
    if (progressTracker) {
      progressTracker.setStage('uploading')
    }

    const maxBytes = chunkedUploadEnabled ? MAX_FILE_BYTES : SINGLE_FILE_MAX_BYTES
    if (file.size > maxBytes) {
      const mb = Math.floor(maxBytes / (1024 * 1024))
      this.reportChunkOutcome('rejected', {
        chunkedUploadEnabled,
        blobSize: file.size,
        maxBytes,
        reason: chunkedUploadEnabled ? 'above_chunked_ceiling' : 'chunked_flag_off'
      })
      throw new Error(
        chunkedUploadEnabled
          ? `File is too large for SmartIntakeAI (max ~${mb} MB). Please try a shorter recording.`
          : `Recording is too large for SmartIntake analysis (max ~${mb} MB). Please try a shorter recording.`
      )
    }

    const useChunked = chunkedUploadEnabled && file.size > SINGLE_FILE_MAX_BYTES

    try {
      if (!useChunked) {
        return await this.uploadSingleFile(file, publicToken, templateId, progressTracker, apiBaseURL)
      }

      this.reportChunkOutcome('start', {
        chunkedUploadEnabled: true,
        blobSize: file.size,
        totalParts: Math.ceil(file.size / CHUNK_PART_MAX_BYTES)
      })

      const result = await this.uploadChunked(file, publicToken, templateId, progressTracker, apiBaseURL)

      this.reportChunkOutcome('success', {
        chunkedUploadEnabled: true,
        blobSize: file.size
      })

      return result
    } catch (error) {
      console.error('Error uploading for analysis:', error)
      const normalized = this.normalizeUploadError(error)
      if (useChunked) {
        this.reportChunkOutcome('failure', {
          chunkedUploadEnabled: true,
          blobSize: file.size,
          code: normalized.code || error.response?.data?.code || null,
          message: normalized.message
        }, normalized)
      }
      throw normalized
    }
  }

  /**
   * LoggerAPI + EventBus so MeetingRoom can Flagmint track / trackError.
   * Only chunked-path outcomes should trip the smartintake_chunked_upload guardrail.
   *
   * @param {'start'|'success'|'failure'|'rejected'} kind
   */
  reportChunkOutcome (kind, overrides = {}, error = null) {
    const extra = {
      blobSize: overrides.blobSize ?? null,
      chunkedUploadEnabled: overrides.chunkedUploadEnabled === true,
      ...overrides
    }
    const message = JSON.stringify(extra)
    try {
      if (kind === 'start') {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'SMARTINTAKE_CHUNK_START', message)
      } else if (kind === 'success') {
        window.LoggerAPI.addLog('client', new Date(), 'log', 'SMARTINTAKE_CHUNK_OK', message)
        eventBus.$emit('smartIntakeChunkOutcome', { type: 'success', extra })
      } else if (kind === 'failure') {
        window.LoggerAPI.addLog('client', new Date(), 'error', 'SMARTINTAKE_CHUNK_FAIL', message)
        eventBus.$emit('smartIntakeChunkOutcome', {
          type: 'failure',
          extra,
          error: error || new Error('SmartIntake chunked upload failed')
        })
      } else if (kind === 'rejected') {
        window.LoggerAPI.addLog('client', new Date(), 'warn', 'SMARTINTAKE_CHUNK_REJECTED', message)
        // Size rejection with flag off is expected fallback behaviour — do not trackError.
        eventBus.$emit('smartIntakeChunkOutcome', { type: 'rejected', extra })
      }
    } catch (err) {
      console.error('Failed to report SmartIntake chunk outcome:', err)
    }
  }

  /**
   * Single-file upload (no part_number).
   */
  async uploadSingleFile (file, publicToken, templateId, progressTracker, apiBaseURL) {
    const formData = new FormData()
    formData.append('file', file, file.name || 'recording.webm')
    formData.append('template_id', templateId)

    const response = await axios.post(
      `${apiBaseURL}/workflow/job/analyze/`,
      formData,
      {
        withCredentials: false,
        headers: {
          'Authorization': `PublicToken ${publicToken}`,
          'X-Client-Context': 'WIDGET'
        },
        timeout: ANALYZE_TIMEOUT_MS,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable && progressTracker) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            progressTracker.setStage('uploading', percent)
          }
        }
      }
    )

    if (progressTracker) {
      progressTracker.setStage('processing')
    }

    return response.data
  }

  /**
   * Chunked upload per SmartIntake AnalyzeRequest:
   * 1. Part 1 with total_parts (creates the upload)
   * 2. Parts 2..N-1 (parallel, retries OK)
   * 3. Last part starts processing (long-running, same response as single-file)
   */
  async uploadChunked (file, publicToken, templateId, progressTracker, apiBaseURL) {
    const parts = this.splitFileIntoParts(file)
    const totalParts = parts.length
    const partSizes = parts.map((p) => p.size)
    const uploadedBytesByPart = new Array(totalParts).fill(0)

    const reportProgress = () => {
      if (!progressTracker) return
      const loaded = uploadedBytesByPart.reduce((sum, n) => sum + n, 0)
      const percent = Math.min(99, Math.round((loaded * 100) / file.size))
      progressTracker.setStage('uploading', percent)
    }

    // Part 1 must succeed first; a failed/duplicate part 1 needs a new Public Token.
    const part1Response = await this.uploadPart({
      blob: parts[0],
      partNumber: 1,
      totalParts,
      publicToken,
      templateId,
      apiBaseURL,
      isLast: totalParts === 1,
      onProgress: (loaded) => {
        uploadedBytesByPart[0] = loaded
        reportProgress()
      }
    })
    uploadedBytesByPart[0] = partSizes[0]
    reportProgress()

    if (totalParts === 1) {
      if (progressTracker) {
        progressTracker.setStage('processing')
      }
      return part1Response
    }

    // Middle parts 2..N-1
    if (totalParts > 2) {
      const middleIndexes = []
      for (let i = 1; i < totalParts - 1; i++) {
        middleIndexes.push(i)
      }
      await this.mapWithConcurrency(middleIndexes, MIDDLE_PART_CONCURRENCY, async (index) => {
        await this.uploadPartWithRetry({
          blob: parts[index],
          partNumber: index + 1,
          publicToken,
          templateId,
          apiBaseURL,
          isLast: false,
          onProgress: (loaded) => {
            uploadedBytesByPart[index] = loaded
            reportProgress()
          }
        })
        uploadedBytesByPart[index] = partSizes[index]
        reportProgress()
      })
    }

    // Last part joins all parts and runs analysis.
    const lastIndex = totalParts - 1
    const analysisResult = await this.uploadLastPartWithMissingRetry({
      parts,
      totalParts,
      publicToken,
      templateId,
      apiBaseURL,
      onProgress: (loaded) => {
        uploadedBytesByPart[lastIndex] = loaded
        reportProgress()
      }
    })
    uploadedBytesByPart[lastIndex] = partSizes[lastIndex]
    reportProgress()

    if (progressTracker) {
      progressTracker.setStage('processing')
    }
    return analysisResult
  }

  /**
   * Split a file into ≤20 MB byte slices (no re-encoding), at most 50 parts.
   * @param {File|Blob} file
   * @returns {Blob[]}
   */
  splitFileIntoParts (file) {
    const totalParts = Math.ceil(file.size / CHUNK_PART_MAX_BYTES)
    if (totalParts > MAX_PARTS) {
      throw new Error(`File requires ${totalParts} parts; SmartIntakeAI allows at most ${MAX_PARTS}`)
    }

    const parts = []
    for (let i = 0; i < totalParts; i++) {
      const start = i * CHUNK_PART_MAX_BYTES
      const end = Math.min(start + CHUNK_PART_MAX_BYTES, file.size)
      parts.push(file.slice(start, end))
    }
    return parts
  }

  /**
   * POST one part to /workflow/job/analyze/.
   * total_parts is sent with part 1 only.
   */
  async uploadPart ({
    blob,
    partNumber,
    totalParts,
    publicToken,
    templateId,
    apiBaseURL,
    isLast,
    onProgress
  }) {
    const formData = new FormData()
    formData.append('file', blob, `part-${partNumber}`)
    formData.append('template_id', templateId)
    formData.append('part_number', String(partNumber))
    if (partNumber === 1 && totalParts != null) {
      formData.append('total_parts', String(totalParts))
    }

    const response = await axios.post(
      `${apiBaseURL}/workflow/job/analyze/`,
      formData,
      {
        withCredentials: false,
        headers: {
          'Authorization': `PublicToken ${publicToken}`,
          'X-Client-Context': 'WIDGET'
        },
        // Last part (and single-part edge case) waits for full analysis.
        timeout: isLast ? ANALYZE_TIMEOUT_MS : 120000,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.loaded != null) {
            onProgress(progressEvent.loaded)
          }
        }
      }
    )

    return response.data
  }

  /**
   * Retry a non-first part on transient failure.
   */
  async uploadPartWithRetry (options) {
    let lastError
    for (let attempt = 1; attempt <= PART_MAX_RETRIES; attempt++) {
      try {
        return await this.uploadPart(options)
      } catch (error) {
        lastError = error
        const code = error.response?.data?.code
        // Permanent for this token — do not retry.
        if (code === 'UPLOAD_CLOSED' || code === 'JOB_ALREADY_EXISTS') {
          throw error
        }
        if (attempt < PART_MAX_RETRIES) {
          await this.wait(Math.pow(2, attempt) * 500)
        }
      }
    }
    throw lastError
  }

  /**
   * Send the last part; if MISSING_PARTS, upload the gaps and resend the last part.
   */
  async uploadLastPartWithMissingRetry ({
    parts,
    totalParts,
    publicToken,
    templateId,
    apiBaseURL,
    onProgress
  }) {
    const lastIndex = totalParts - 1
    let attempts = 0
    const maxMissingRounds = 3

    while (attempts < maxMissingRounds) {
      attempts++
      try {
        return await this.uploadPartWithRetry({
          blob: parts[lastIndex],
          partNumber: totalParts,
          publicToken,
          templateId,
          apiBaseURL,
          isLast: true,
          onProgress
        })
      } catch (error) {
        const data = error.response?.data
        if (data?.code === 'MISSING_PARTS' && Array.isArray(data.missingParts) && data.missingParts.length) {
          await this.mapWithConcurrency(data.missingParts, MIDDLE_PART_CONCURRENCY, async (partNumber) => {
            const index = partNumber - 1
            await this.uploadPartWithRetry({
              blob: parts[index],
              partNumber,
              publicToken,
              templateId,
              apiBaseURL,
              isLast: false
            })
          })
          continue
        }
        throw error
      }
    }

    throw new Error('Chunked upload failed: missing parts could not be resolved')
  }

  /**
   * Run async work over items with a concurrency limit.
   */
  async mapWithConcurrency (items, concurrency, worker) {
    const queue = [...items]
    const runners = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (queue.length) {
        const item = queue.shift()
        await worker(item)
      }
    })
    await Promise.all(runners)
  }

  normalizeUploadError (error) {
    if (error.response?.data) {
      const data = error.response.data
      const message = data.details || data.error || error.message
      const err = new Error(`Upload failed: ${message}`)
      err.code = data.code
      err.retry = data.retry
      err.response = error.response
      return err
    }
    if (error.message?.startsWith('Upload failed:') || error.message?.startsWith('File ')) {
      return error
    }
    return new Error(`Upload failed: ${error.message}`)
  }

  /**
   * Fetch analysis results from SmartIntakeAI
   *
   * @param {string} publicToken - The public token
   * @returns {Promise<Object>} Analysis results
   */
  async getResults (publicToken, apiBaseURL) {
    try {
      const response = await this.axiosClient.get(
        `/workflow/public-token/${publicToken}/results/`,
        {
          headers: {
            'Authorization': `Bearer ${this.store.state?.access_token}`,
            'X-Client-Context': 'WIDGET'
          }
        }
      )

      return response.data
    } catch (error) {
      if (error.response?.status === 422) {
        // Results not ready yet, return null to indicate polling needed
        return null
      }
      console.error('Error fetching results:', error)
      throw new Error(`Failed to fetch results: ${error.message}`)
    }
  }

  /**
   * Main workflow: Create token → Upload → Analyze
   *
   * Templates are pre-configured in admin settings.
   *
   * @param {File} file - The audio file to analyze
   * @param {string} fileName - The file name
   * @param {Object} options - Configuration options
   * @param {Object} options.transcriptionService - Service configuration with templateId
   * @param {number} options.transcriptionService.templateId - The pre-configured template ID
   * @param {number} options.transcriptionService.recordingDurationSeconds - Recording duration in seconds
   * @param {Object} options.progressToast - Progress toast element
   * @param {UploadProgressTracker} options.progressTracker - Progress tracker instance
   * @returns {Promise<Object>} Analysis results with publicToken
   */
  async analyzeAudio (file, fileName, options = {}) {
    const { progressTracker, transcriptionService, chunkedUploadEnabled = false } = options
    const templateId = transcriptionService?.smartintake_template
    const recordingDurationSeconds = transcriptionService?.recordingDurationSeconds

    if (!templateId) {
      throw new Error('Template ID must be configured in transcription service settings')
    }

    // Part 1 cannot be retried on the same token (JOB_ALREADY_EXISTS). Restart with a new token.
    let lastError
    for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
      try {
        if (progressTracker) {
          progressTracker.setStage('requesting_token')
        }

        const { publicToken, apiUrl } = await this.requestPublicToken({
          webhookUrl: `${process.env.VUE_APP_API_HOST}/admin/webhooks/smartIntakeOnWebhookNotification`,
          deleteAfterAccessed: true,
          recordingDurationSeconds
        })

        if (progressTracker) {
          progressTracker.setStage('token_ready')
          await this.wait(300)
        }

        const analysisResult = await this.uploadForAnalysis(
          file,
          publicToken,
          templateId,
          progressTracker,
          apiUrl,
          chunkedUploadEnabled
        )

        if (progressTracker) {
          progressTracker.complete()
        }

        return {
          publicToken,
          templateId,
          durationInMinutes: recordingDurationSeconds ? recordingDurationSeconds / 60 : undefined,
          analysis: analysisResult,
          status: 'submitted'
        }
      } catch (error) {
        lastError = error
        const code = error.code || error.response?.data?.code
        const shouldRestart =
          code === 'JOB_ALREADY_EXISTS' ||
          code === 'UPLOAD_CLOSED' ||
          (error.response?.status === 400 && error.message?.includes('Part 1'))

        if (!shouldRestart || attempt >= this.MAX_RETRIES) {
          console.error('Analysis workflow failed:', error)
          throw error
        }

        console.warn(`SmartIntakeAI upload attempt ${attempt} failed (${code || error.message}); requesting a new Public Token`)
        if (progressTracker?.retry) {
          progressTracker.retry(attempt, this.MAX_RETRIES)
        }
        await this.wait(Math.pow(2, attempt) * 500)
      }
    }

    throw lastError
  }

  /**
   * Poll for analysis results with exponential backoff
   *
   * @param {string} publicToken - The public token
   * @param {number} maxAttempts - Maximum polling attempts (default: 60)
   * @param {number} initialDelay - Initial delay in ms (default: 2000)
   * @returns {Promise<Object>} Analysis results when ready
   */
  async pollForResults (publicToken, maxAttempts = 60, initialDelay = 2000) {
    let attempt = 0
    let delay = initialDelay

    while (attempt < maxAttempts) {
      try {
        const results = await this.getResults(publicToken)

        if (results) {
          console.log('Analysis results ready:', results)
          return results
        }

        // Not ready yet, wait and retry
        await this.wait(delay)
        delay = Math.min(delay * 1.5, 30000) // Cap at 30 seconds
        attempt++
      } catch (error) {
        if (error.message.includes('expired') || error.message.includes('already accessed')) {
          throw error
        }
        attempt++
      }
    }

    throw new Error('Analysis results polling timeout')
  }

  /**
   * Waits for a specified delay in milliseconds
   * @param {number} delay - The delay in milliseconds
   * @returns {Promise<void>}
   */
  wait (delay) {
    return new Promise((resolve) => setTimeout(resolve, delay))
  }
}

/**
 * UploadProgressTracker - Self-contained progress tracking for multi-stage uploads
 *
 * Provides visual feedback for:
 * 1. Token generation
 * 2. Token received
 * 3. Upload progress
 * 4. Completion
 *
 * Works with existing toast/progress UI elements
 */
class UploadProgressTracker {
  constructor (progressToast) {
    this.toast = progressToast
    this.stages = {
      'init': { label: 'Preparing analysis...', percent: 0, color: 'blue' },
      'requesting_token': { label: 'Requesting token...', percent: 5, color: 'blue' },
      'token_ready': { label: 'Token received ✓', percent: 10, color: 'blue' },
      'uploading': { label: 'Uploading to SmartIntakeAI', percent: 20, color: 'blue' },
      'processing': { label: 'Processing...', percent: 95, color: 'green' },
      'complete': { label: 'Upload complete ✓', percent: 100, color: 'green' }
    }
    this.currentStage = 'init'
    this.uploadPercent = 0
  }

  setStage (stage, uploadPercent = null) {
    this.currentStage = stage
    const stageInfo = this.stages[stage]

    if (stage === 'uploading' && uploadPercent !== null) {
      const mappedPercent = 15 + (uploadPercent * 0.80)
      this.uploadPercent = uploadPercent
      this.update(
        `${stageInfo.label} (${uploadPercent}%)`,
        Math.round(mappedPercent),
        stageInfo.color
      )
    } else {
      this.update(stageInfo.label, stageInfo.percent, stageInfo.color)
    }
  }

  update (label, percent, color = 'blue') {
    if (!this.toast || !this.toast[0]) return

    const progressElement = this.toast[0].querySelector('progress')
    const title = this.toast[0].querySelector('#uploading-title')
    const subtitle = this.toast[0].querySelector('#uploading-subtitle')

    if (title) {
      title.textContent = label
    }

    if (progressElement) {
      progressElement.value = percent
      progressElement.style.display = 'block'
      progressElement.style.backgroundColor = color
    }

    if (subtitle && percent > 0) {
      subtitle.textContent = `${Math.round(percent)}%`
    }
  }

  complete () {
    this.setStage('complete')
    setTimeout(() => {
      if (this.toast && this.toast.toast) {
        this.toast.toast('hide')
      }
    }, 2000)
  }

  error (message) {
    this.update(`Error: ${message}`, 0, 'red')
    const progressElement = this.toast?.[0]?.querySelector('progress')
    if (progressElement) {
      progressElement.style.backgroundColor = 'red'
    }
  }

  retry (attempt, maxRetries) {
    const backoffSeconds = Math.pow(2, attempt)
    this.update(
      `Upload failed, retrying (${attempt}/${maxRetries}) in ${backoffSeconds}s...`,
      10,
      'orange'
    )
  }
}

/**
 * MultiStepProgressAdapter - Adapter for MultiStepUpload Vue component
 *
 * Maps SmartIntakeAI analysis stages to the MultiStepUpload component:
 * - Step 0: Requesting token
 * - Step 1: Token received
 * - Step 2: Uploading audio
 * - Step 3: Complete
 */
class MultiStepProgressAdapter {
  constructor (multiStepComponent) {
    this.component = multiStepComponent
  }

  setStage (stage, uploadPercent = null) {
    if (!this.component) return

    switch (stage) {
      case 'init':
      case 'requesting_token':
        break
      case 'token_ready':
        this.component.nextStep()
        break
      case 'uploading':
        if (uploadPercent !== null) {
          this.component.setUploadProgress(uploadPercent)
        }
        break
      case 'processing':
      case 'complete':
        this.component.complete()
        break
    }
  }

  update (label, percent, color = 'blue') {
    // Not used in MultiStepUpload
  }

  complete () {
    if (this.component) {
      this.component.complete()
    }
  }

  error (message) {
    if (this.component) {
      this.component.error(message)
    }
  }

  retry (attempt, maxRetries) {
    const backoffSeconds = Math.pow(2, attempt)
    if (this.component) {
      this.component.error(`Retry ${attempt}/${maxRetries} in ${backoffSeconds}s`)
    }
  }
}

export default SmartIntakeAIUploader
